import { DynamicFurnitureSVG } from "../map/shapes/DynamicFurnitureShape/DynamicFurnitureShape";
import React from "react";
import {
  CircleShape,
  FurnitureShape,
  GroupShape,
  IconShape,
  ImageShape,
  WallPathShape as IWallPathShape,
  LayerType,
  LegendBridgeData,
  LegendZoneCategoryData,
  RectangleShape,
  Shape,
  SpotShape,
  SpotShapeType,
  SVGShape,
  TextShape,
  ViewSVGLegendHandle,
  WallShape,
  ZoneShape
} from "../../types";
import {
  bezierArcLength,
  bezierParameterAtDistance,
  bezierPointAndNormal,
  getWallControlPoint,
  quadraticBezierPoint
} from "../../utils/bezier";
import { stripHexAlpha } from "../../utils/color";
import {
  getCurvedWallSegmentParams,
  getWallDash,
  getWallSegments
} from "../../utils/wall";
import { computeWallFloorPolygons } from "../../utils/wallFloorFill";
import { findWallJunctions } from "../../utils/wallJunction";
import { getAllWallSegments } from "../../utils/wallPathSegments";
import {
  computeZoneAreaPx,
  sampleZonePolygonPoints
} from "../../utils/zonePolygon";
import {
  useMapCanvas,
  useMapCanvasShapes,
  useMapCanvasStagePosition
} from "../map/contexts/MapCanvasContext";
import {
  DEFAULT_DOOR_COLOR,
  DEFAULT_ZONE_BORDER_COLOR,
  DEFAULT_ZONE_BORDER_WIDTH,
  DEFAULT_ZONE_FILL_OPACITY,
  HEAT_ZONE_FILL_OPACITY
} from "../map/defines";
import { FurnitureShape as FurnitureShapeComponent } from "../map/shapes/FurnitureShape/FurnitureShape";
import { IconShape as IconShapeComponent } from "../map/shapes/IconShape/IconShape";
import { ShapeProvider } from "../map/shapes/ShapeContext/ShapeProvider";
import { SpotShapeComponent } from "../map/shapes/SpotShape/SpotShape";
import { ZoneLegend } from "../ZoneLegend/ZoneLegend";
import { IconLinkPopover } from "./IconLinkPopover";
import { MultilineSvgText } from "./MultilineSvgText";
import { SVGShapeRenderer } from "./SVGShapeRenderer";
import { ZoneHoverTooltip } from "./ZoneHoverTooltip";

export interface ViewSVGProps {
  svgOverlay?: React.ReactNode;
  /** Crop the rendering to this canvas-coordinate rectangle (plus a small
   *  margin): the SVG fills its container and shows just that part of the
   *  map, ignoring pan/zoom. Used for region previews in Settings. */
  focusBounds?: { x: number; y: number; width: number; height: number };
  showOutside?: boolean;
  style?: React.SVGProps<SVGSVGElement>["style"];
  /** Suppress the zone-type legend overlay. The host app sets this when
   *  the map editor is open so the legend (which is portaled to
   *  document.body and would otherwise stay visible above the editor
   *  modal) doesn't bleed into the editing experience. Also set (together
   *  with `bridgeZoneLegend`) by hosts that render their own legend UI
   *  from the bridged data instead. */
  hideZoneLegend?: boolean;
  /** Forwarded to ZoneLegend — see its `bottomOffset` doc. */
  legendBottomOffset?: number | string;
  /** Bridges the legend's content and controls out to `onLegendDataChange`
   *  / `legendControlRef` instead of only driving the in-webview panel.
   *  Sending the data is not gated on `hideZoneLegend` — a host could in
   *  principle want both — but every current caller sets them together. */
  bridgeZoneLegend?: boolean;
  /** Fires with the current legend content whenever the underlying map
   *  data or the viewer's own visibility/hover state changes. Only
   *  meaningful alongside `bridgeZoneLegend`. */
  onLegendDataChange?: (data: LegendBridgeData) => void;
  /** Lets a host imperatively drive the legend's own state (toggle a
   *  category, show/hide all, hover-highlight, follow a linked zone) —
   *  the same actions the in-webview ZoneLegend triggers on itself. */
  legendControlRef?: React.RefObject<ViewSVGLegendHandle | null>;
}

// Build the SVG transform string that matches Konva's `T(x,y) · R(θ) · S(sX, sY)`
// pipeline for shapes whose content is rendered at WORLD coords (rect, circle,
// image, etc. — anchored via `x={shape.x}` directly). The trailing
// `translate(-x, -y)` moves world-positioned content into local space so the
// rotate+scale composes around the shape's origin, just like Konva.
const buildWorldShapeTransform = (shape: {
  x: number;
  y: number;
  rotation?: number;
  scaleX?: number;
  scaleY?: number;
}): string | undefined => {
  const rot = shape.rotation || 0;
  const sX = shape.scaleX || 1;
  const sY = shape.scaleY || 1;
  if (rot === 0 && sX === 1 && sY === 1) return undefined;
  return `translate(${shape.x}, ${shape.y}) rotate(${rot}) scale(${sX}, ${sY}) translate(${-shape.x}, ${-shape.y})`;
};

interface RenderOptions {
  zoneTypes?: { id: string; name: string; color: string }[];
  /** Per-zone fill override (campus heatmaps): zone id -> CSS colour.
   *  Wins over the category colour and paints the zone solid enough to
   *  read as data. */
  zoneColors?: Record<string, string>;
  /** Host-app handler for zones with a navigation link. When provided,
   *  linked zones become clickable (cursor + click → handler). */
  onZoneLinkClick?: (link: NonNullable<ZoneShape["link"]>) => void;
  /** Zone id currently hovered (on the map or in the legend) — the zone
   *  brightens so it reads as an interactive target. */
  hoveredLinkZoneId?: string | null;
  /** Reports hover over a linked zone on the map (null on leave). */
  onLinkZoneHover?: (zoneId: string | null) => void;
  /** Host handler for a click on a linked icon. `rect` is the clicked
   *  icon's real viewport bounding box (from `getBoundingClientRect`),
   *  already accounting for pan/zoom and any ancestor CSS scaling —
   *  used to position the description popover. Decides whether to
   *  navigate immediately or open that popover first. */
  onLinkedIconClick?: (icon: IconShape, rect: DOMRect) => void;
  /** Categories the user has activated in the legend. Zones whose
   *  `categoryTypeId` is NOT in this set are skipped — by default the
   *  set is empty so all category-tagged zones stay hidden until the
   *  viewer explicitly turns them on. Zones without a categoryTypeId
   *  always render (they're not part of the legend). */
  visibleZoneCategories?: Set<string>;
  /** When set (legend points-of-interest hover), icons with this
   *  categoryTypeId render with a highlight halo so they're easy to
   *  locate on the map. */
  iconCategoryHover?: string | null;
  /** When set (legend hover), zones with this categoryTypeId render
   *  brighter than the other currently-visible zones. Hover is purely
   *  visual — it doesn't toggle visibility. */
  zoneCategoryHover?: string | null;
  /** Reports mouse position (viewport coords) while hovering any zone —
   *  drives the info tooltip (name/category/area). `null` on leave. */
  onZoneTooltipHover?: (
    info: { zone: ZoneShape; clientX: number; clientY: number } | null
  ) => void;
}

const renderShapeToSVG = (
  shape: Shape,
  options: RenderOptions = {}
): React.ReactElement | null => {
  const baseProps = { x: shape.x, y: shape.y };
  const opacity = 1;
  const opacityAttr = opacity !== 1 ? { opacity } : {};

  // Common ShapeProvider props for all shapes that need it
  const shapeProviderProps = {
    shape,
    isSelected: false,
    isInSelection: false,
    onSelect: () => {},
    onUpdate: () => {},
    snapToGrid: false,
    gridSize: 20,
    otherShapes: [],
    showSnapLines: false,
    disabled: false,
    isMultiSelected: false,
    onDelete: () => {},
    onLocate: () => {},
    onMoveUp: () => {},
    onMoveDown: () => {}
  };

  // Common component props for shapes that need them
  const componentProps = {
    id: shape.id,
    x: shape.x,
    y: shape.y,
    rotation: shape.rotation || 0,
    draggable: false,
    onClick: () => {},
    onTap: () => {},
    onDragStart: () => {},
    onDragMove: () => {},
    onDragEnd: () => {},
    onTransformEnd: () => {},
    onMouseEnter: () => {},
    onMouseLeave: () => {},
    onContextMenu: () => {},
    ref: null
  };

  const renderShapeContent = () => {
    switch (shape.type) {
      case "rectangle": {
        const rectShape = shape as RectangleShape;
        const rTransform = buildWorldShapeTransform(rectShape);
        const rawRx = rectShape.cornerRadius || 0;
        const maxRx = Math.min(rectShape.width, rectShape.height) / 2;
        const clampedRx = Math.min(rawRx, maxRx);
        return (
          <rect
            {...baseProps}
            width={rectShape.width}
            height={rectShape.height}
            fill={rectShape.noFill ? "none" : rectShape.color || "#000000"}
            stroke={rectShape.stroke || "none"}
            strokeWidth={rectShape.strokeWidth || 0}
            {...opacityAttr}
            rx={clampedRx}
            {...(rTransform ? { transform: rTransform } : {})}
          />
        );
      }

      case "circle": {
        const circleShape = shape as CircleShape;
        const cx = circleShape.x;
        const cy = circleShape.y;
        const cTransform = buildWorldShapeTransform(circleShape);
        return (
          <ellipse
            cx={cx}
            cy={cy}
            rx={circleShape.width / 2}
            ry={circleShape.height / 2}
            fill={circleShape.noFill ? "none" : circleShape.color || "#000000"}
            stroke={circleShape.stroke || "none"}
            strokeWidth={circleShape.strokeWidth || 0}
            {...opacityAttr}
            {...(cTransform ? { transform: cTransform } : {})}
          />
        );
      }

      case "text": {
        const textShape = shape as TextShape;
        const fontSize = textShape.fontSize || 20;
        const fontFamily = textShape.fontFamily || "Quicksand";
        // Konva renders "normal" at weight 400 — the viewer must measure
        // and draw with the same weight or lines wrap differently.
        const fontWeight = textShape.isBold ? "700" : "400";
        const fontStyle = textShape.isItalic ? "italic" : "normal";
        const textDecoration = textShape.isUnderline ? "underline" : "none";
        const textColor = textShape.textColor || "#B5B5B5";
        const textAlign = textShape.textAlign || "left";

        // Calculate text anchor based on alignment
        let textAnchor = "start";
        if (textAlign === "center") textAnchor = "middle";
        else if (textAlign === "right") textAnchor = "end";

        const tTransform = buildWorldShapeTransform(textShape);
        return (
          <g {...(tTransform ? { transform: tTransform } : {})}>
            <MultilineSvgText
              width={textShape.width}
              style={{
                fontSize: `${fontSize}px`,
                fontFamily: fontFamily,
                fontWeight: fontWeight,
                fontStyle,
                textDecoration,
                fill: textColor
              }}
              textAnchor={textAnchor as never}
              height={textShape.height}
              fontFamily={fontFamily}
              {...baseProps}
            >
              {textShape.text}
            </MultilineSvgText>
          </g>
        );
      }

      case "svg": {
        const svgShape = shape as SVGShape;
        return <SVGShapeRenderer shape={svgShape} />;
      }

      case "icon": {
        const iconShape = shape as IconShape;
        const isPoiHighlighted =
          !!iconShape.categoryTypeId &&
          options.iconCategoryHover === iconShape.categoryTypeId;

        const iconLink = iconShape.link;
        const handleIconLinkClick =
          iconLink && options.onLinkedIconClick
            ? (event: React.MouseEvent<SVGGElement>) => {
                event.preventDefault();
                event.stopPropagation();
                // Real viewport coordinates — already account for pan/zoom
                // and any ancestor CSS scaling (e.g. a "fit to container"
                // wrapper), unlike computing position from world x/y.
                options.onLinkedIconClick?.(
                  iconShape,
                  event.currentTarget.getBoundingClientRect()
                );
              }
            : undefined;

        const iconContent = !isPoiHighlighted ? (
          <IconShapeComponent {...componentProps} renderMode="svg" />
        ) : (
          // Legend hover: pulse a halo in the category color around the icon
          // so it's easy to locate (icons stay always-visible; the halo is
          // purely a locator).
          (() => {
            const poiColor =
              options.zoneTypes?.find((t) => t.id === iconShape.categoryTypeId)
                ?.color || "#2563eb";
            const haloPad = 8;
            const haloTransform = buildWorldShapeTransform(iconShape);
            return (
              <>
                <rect
                  x={iconShape.x - haloPad}
                  y={iconShape.y - haloPad}
                  width={iconShape.width + haloPad * 2}
                  height={iconShape.height + haloPad * 2}
                  rx={10}
                  fill={poiColor}
                  fillOpacity={0.18}
                  stroke={poiColor}
                  strokeWidth={2.5}
                  {...(haloTransform ? { transform: haloTransform } : {})}
                >
                  <animate
                    attributeName="stroke-opacity"
                    values="1;0.35;1"
                    dur="1.6s"
                    repeatCount="indefinite"
                  />
                </rect>
                <IconShapeComponent {...componentProps} renderMode="svg" />
              </>
            );
          })()
        );

        if (!handleIconLinkClick) return iconContent;

        // Clickable icons: same navigation mechanism as zones. Kept out of
        // the legend entirely — the only affordance is a hover tooltip and
        // pointer cursor on the icon itself.
        return (
          <g
            onClick={handleIconLinkClick}
            // Stop this from bubbling to IconLinkPopover's document-level
            // "click outside to close" listener — pointerdown fires before
            // click, so without this, re-clicking the icon that opened the
            // popover closes it via the outside-click handler BEFORE this
            // element's own onClick toggle runs, making the popover
            // immediately reopen instead of staying closed.
            onPointerDown={(event) => event.stopPropagation()}
            style={{ cursor: "pointer" }}
          >
            <title>
              {iconLink?.label ||
                (iconLink?.description ? "View details" : iconLink?.url) ||
                "Open map"}
            </title>
            {iconContent}
          </g>
        );
      }

      case "dynamic-furniture": return <DynamicFurnitureSVG shape={shape} />;
      case "furniture": {
        const furnitureShape = shape as FurnitureShape;
        return <FurnitureShapeComponent {...componentProps} renderMode="svg" />;
      }

      case "image": {
        const imageShape = shape as ImageShape;
        const iTransform = buildWorldShapeTransform(imageShape);
        return (
          <image
            {...baseProps}
            width={imageShape.width}
            height={imageShape.height}
            href={imageShape.path}
            {...opacityAttr}
            {...(iTransform ? { transform: iTransform } : {})}
          />
        );
      }

      case "spot": {
        const spotShape = shape as SpotShape;
        const companions = spotShape.companions;
        if (!companions?.length) {
          return <SpotShapeComponent {...componentProps} renderMode="svg" />;
        }

        // Render spot + companions in a fragment. Companions have positions
        // relative to the spot origin, so wrap them in a group that
        // translates to the spot's position (with rotation/scale if applicable).
        // The content is at LOCAL origin so we use Konva's `T · R · S` order
        // directly without the trailing `translate(-x, -y)`.
        const spotSx = spotShape.scaleX || 1;
        const spotSy = spotShape.scaleY || 1;
        const spotRotPart = spotShape.rotation
          ? ` rotate(${spotShape.rotation})`
          : "";
        const spotScalePart =
          spotSx !== 1 || spotSy !== 1 ? ` scale(${spotSx}, ${spotSy})` : "";
        const companionTransform = `translate(${spotShape.x}, ${spotShape.y})${spotRotPart}${spotScalePart}`;
        return (
          <React.Fragment key={shape.id}>
            <g transform={companionTransform}>
              {companions.map((companion) => {
                // Render each companion at its relative position
                return (
                  <React.Fragment key={companion.id}>
                    {renderShapeToSVG(companion, options)}
                  </React.Fragment>
                );
              })}
            </g>
            <SpotShapeComponent {...componentProps} renderMode="svg" />
          </React.Fragment>
        );
      }

      case "zone": {
        const zoneShape = shape as ZoneShape;
        // Category visibility: zones with a categoryTypeId stay hidden
        // until the user toggles the corresponding chip in the legend.
        // Zones without a categoryTypeId render normally (they're not
        // part of the legend so they aren't part of this filter).
        // A zone given an explicit colour (heatmaps, region previews) is
        // always drawn.
        if (zoneShape.categoryTypeId && !options.zoneColors?.[zoneShape.id]) {
          if (!options.visibleZoneCategories?.has(zoneShape.categoryTypeId)) {
            return null;
          }
        }
        // When a zone references a category, override its fill + border
        // with the category's color so the legend swatches match the
        // painted zones on the map. Falls back to per-shape colors when
        // no category is assigned.
        const category = zoneShape.categoryTypeId
          ? options.zoneTypes?.find((t) => t.id === zoneShape.categoryTypeId)
          : undefined;
        // Opacity is controlled exclusively by fillOpacity — strip any
        // alpha baked into the color (e.g. the old '#3B82F620' default)
        // so the two don't multiply into a nearly-invisible fill.
        const heatColor = options.zoneColors?.[zoneShape.id];
        const zoneFillRaw = heatColor || category?.color || zoneShape.color;
        const zoneFill = zoneFillRaw ? stripHexAlpha(zoneFillRaw) : zoneFillRaw;
        const zoneStroke =
          heatColor ||
          category?.color ||
          zoneShape.borderColor ||
          DEFAULT_ZONE_BORDER_COLOR;

        // Hover spotlight: when a legend chip is hovered, every zone
        // that doesn't share its categoryTypeId fades out so the matching
        // ones pop. Matching zones (and zones of the hovered category
        // specifically) get a small opacity bump so the highlight is
        // unambiguous even on dense maps. Wrapping the whole group in a
        // single opacity value keeps the rect AND label dimming together.
        const baseFillOpacity = heatColor
          ? HEAT_ZONE_FILL_OPACITY
          : (zoneShape.fillOpacity ?? DEFAULT_ZONE_FILL_OPACITY);
        let groupOpacity = 1;
        let effectiveFillOpacity = baseFillOpacity;
        if (
          options.zoneCategoryHover !== undefined &&
          options.zoneCategoryHover !== null
        ) {
          const matchesHover =
            zoneShape.categoryTypeId === options.zoneCategoryHover;
          if (matchesHover) {
            // Brighten the fill so the match really pops.
            effectiveFillOpacity = Math.min(1, baseFillOpacity * 3);
          } else {
            groupOpacity = 0.2;
          }
        }
        // Linked (clickable) zones must be discoverable at rest — enforce
        // a minimum fill so they never blend invisibly into the floor.
        if (zoneShape.link) {
          effectiveFillOpacity = Math.max(effectiveFillOpacity, 0.2);
        }
        // Linked-zone hover (map or legend): brighten so the zone reads
        // as a clickable target.
        if (options.hoveredLinkZoneId === zoneShape.id) {
          effectiveFillOpacity = Math.min(
            1,
            Math.max(baseFillOpacity * 3, 0.45)
          );
        }
        const zRawRx = zoneShape.cornerRadius || 0;
        const zMaxRx = Math.min(zoneShape.width, zoneShape.height) / 2;
        const zClampedRx = Math.min(zRawRx, zMaxRx);
        const zTransform = buildWorldShapeTransform(zoneShape);
        // Polygon zones: sampled outline (curved edges flattened) in world
        // coords. Falls back to the rectangle when absent.
        const zonePolygonPoints =
          (zoneShape.vertices?.length ?? 0) >= 3
            ? sampleZonePolygonPoints(
                zoneShape.vertices as { x: number; y: number }[],
                zoneShape.edges
              )
                .map((p) => `${zoneShape.x + p.x},${zoneShape.y + p.y}`)
                .join(" ")
            : null;
        const zoneLink = zoneShape.link;
        const handleZoneLinkClick =
          zoneLink && options.onZoneLinkClick
            ? (event: React.MouseEvent<SVGGElement>) => {
                event.preventDefault();
                event.stopPropagation();
                options.onZoneLinkClick?.(zoneLink);
              }
            : undefined;
        const handleZoneMouseEnter = (event: React.MouseEvent<SVGGElement>) => {
          if (handleZoneLinkClick) options.onLinkZoneHover?.(zoneShape.id);
          options.onZoneTooltipHover?.({
            zone: zoneShape,
            clientX: event.clientX,
            clientY: event.clientY
          });
        };
        const handleZoneMouseMove = (event: React.MouseEvent<SVGGElement>) => {
          options.onZoneTooltipHover?.({
            zone: zoneShape,
            clientX: event.clientX,
            clientY: event.clientY
          });
        };
        const handleZoneMouseLeave = () => {
          if (handleZoneLinkClick) options.onLinkZoneHover?.(null);
          options.onZoneTooltipHover?.(null);
        };
        return (
          <g
            key={zoneShape.id}
            transform={zTransform}
            opacity={groupOpacity}
            onClick={handleZoneLinkClick}
            onMouseEnter={handleZoneMouseEnter}
            onMouseMove={handleZoneMouseMove}
            onMouseLeave={handleZoneMouseLeave}
            style={{
              transition: "opacity 120ms ease-out",
              cursor: handleZoneLinkClick ? "pointer" : undefined
            }}
          >
            {handleZoneLinkClick ? (
              <title>
                {zoneLink?.label ||
                  zoneLink?.url ||
                  zoneShape.name ||
                  "Open map"}
              </title>
            ) : null}
            {zonePolygonPoints ? (
              <polygon
                points={zonePolygonPoints}
                fill={zoneFill}
                fillOpacity={effectiveFillOpacity}
                stroke={zoneStroke}
                strokeWidth={zoneShape.borderWidth || DEFAULT_ZONE_BORDER_WIDTH}
                strokeDasharray="8 4"
              />
            ) : (
              <rect
                x={zoneShape.x}
                y={zoneShape.y}
                width={zoneShape.width}
                height={zoneShape.height}
                fill={zoneFill}
                // fill-opacity only — keep the border at full opacity so
                // the category color reads strongly on the outline while
                // the fill stays translucent.
                fillOpacity={effectiveFillOpacity}
                stroke={zoneStroke}
                strokeWidth={zoneShape.borderWidth || DEFAULT_ZONE_BORDER_WIDTH}
                strokeDasharray="8 4"
                rx={zClampedRx}
              />
            )}
            {(() => {
              // Name placement within the zone bounds (default top-left).
              // Same 8px padding + text-top math as the Konva editor, and
              // the same word-wrap width — so the name stays inside the
              // rectangle in both instead of bleeding out of the viewer.
              const labelPosition = zoneShape.labelPosition || "top-left";
              const labelFontSize = zoneShape.labelFontSize || 14;
              const isCenteredX = labelPosition.endsWith("center");
              const labelTop =
                labelPosition === "center"
                  ? zoneShape.y + zoneShape.height / 2 - labelFontSize / 2
                  : labelPosition.startsWith("bottom")
                    ? zoneShape.y + zoneShape.height - 8 - labelFontSize
                    : zoneShape.y + 8;
              return (
                <MultilineSvgText
                  x={zoneShape.x + 8}
                  y={labelTop}
                  width={Math.max(zoneShape.width - 16, labelFontSize)}
                  // Konva doesn't clip wrapped label lines to the zone
                  // height, so neither do we — generous height, no clip.
                  height={labelFontSize * 1000}
                  textAnchor={isCenteredX ? "middle" : "start"}
                  fontFamily="Quicksand"
                  style={{
                    fontSize: `${labelFontSize}px`,
                    fontFamily: "Quicksand",
                    fontWeight: "400",
                    fill: zoneStroke
                  }}
                >
                  {zoneShape.name || ""}
                </MultilineSvgText>
              );
            })()}
          </g>
        );
      }

      case "wall": {
        const wallShape = shape as WallShape;
        const wdx = wallShape.x2 - wallShape.x;
        const wdy = wallShape.y2 - wallShape.y;
        const wLen = Math.sqrt(wdx * wdx + wdy * wdy);
        const wux = wLen > 0 ? wdx / wLen : 0;
        const wuy = wLen > 0 ? wdy / wLen : 0;
        const hasDoors = wallShape.doors && wallShape.doors.length > 0;
        const wCurvature = wallShape.curvature || 0;
        const wCurvatureCenter = wallShape.curvatureCenter ?? 0.5;
        const wDash = getWallDash(wallShape.dashPattern, wallShape.thickness);
        const wDashStr = wDash?.join(",");
        const wColor = wallShape.color || "#D1D5D9";
        const wCapStart = wallShape.lineCapStart || "butt";
        const wCapEnd = wallShape.lineCapEnd || "butt";

        if (wCurvature === 0) {
          // Straight wall
          const segments = getWallSegments(wallShape.doors, wLen);
          return (
            <g key={wallShape.id}>
              {wallShape.doors?.map((door) => {
                if (wLen === 0 || door.transparent) return null;
                const hw = door.width / 2;
                const cd = door.position * wLen;
                return (
                  <line
                    key={door.id}
                    x1={wallShape.x + (cd - hw) * wux}
                    y1={wallShape.y + (cd - hw) * wuy}
                    x2={wallShape.x + (cd + hw) * wux}
                    y2={wallShape.y + (cd + hw) * wuy}
                    stroke={DEFAULT_DOOR_COLOR}
                    strokeWidth={wallShape.thickness / 2}
                    strokeLinecap="butt"
                  />
                );
              })}

              {segments.map(([start, end], i) => (
                <line
                  key={`seg-${i}`}
                  x1={wallShape.x + start * wux}
                  y1={wallShape.y + start * wuy}
                  x2={wallShape.x + end * wux}
                  y2={wallShape.y + end * wuy}
                  stroke={wColor}
                  strokeWidth={wallShape.thickness}
                  strokeLinecap={hasDoors ? "round" : "butt"}
                  strokeDasharray={wDashStr}
                />
              ))}
              {wallShape.windows?.map((win) => {
                if (wLen === 0) return null;
                const hw = win.width / 2;
                const cd = win.position * wLen;
                return (
                  <line
                    key={win.id}
                    x1={wallShape.x + (cd - hw) * wux}
                    y1={wallShape.y + (cd - hw) * wuy}
                    x2={wallShape.x + (cd + hw) * wux}
                    y2={wallShape.y + (cd + hw) * wuy}
                    stroke={DEFAULT_DOOR_COLOR}
                    strokeWidth={
                      wallShape.thickness - (wallShape.thickness * 20) / 100
                    }
                    strokeLinecap="butt"
                  />
                );
              })}
              {wCapStart === "round" && (
                <circle
                  cx={wallShape.x}
                  cy={wallShape.y}
                  r={wallShape.thickness / 2}
                  fill={wColor}
                />
              )}
              {wCapEnd === "round" && (
                <circle
                  cx={wallShape.x2}
                  cy={wallShape.y2}
                  r={wallShape.thickness / 2}
                  fill={wColor}
                />
              )}
            </g>
          );
        }

        // Curved wall
        const { cx: wcx, cy: wcy } = getWallControlPoint(
          wallShape.x,
          wallShape.y,
          wallShape.x2,
          wallShape.y2,
          wCurvature,
          wCurvatureCenter
        );
        const segParams = getCurvedWallSegmentParams(
          wallShape.doors,
          wallShape.x,
          wallShape.y,
          wcx,
          wcy,
          wallShape.x2,
          wallShape.y2
        );

        return (
          <g key={wallShape.id}>
            {wallShape.doors?.map((door) => {
              if (wLen === 0 || door.transparent) return null;
              const arcLen = bezierArcLength(
                wallShape.x,
                wallShape.y,
                wcx,
                wcy,
                wallShape.x2,
                wallShape.y2
              );
              const doorDist = door.position * arcLen;
              const t = bezierParameterAtDistance(
                wallShape.x,
                wallShape.y,
                wcx,
                wcy,
                wallShape.x2,
                wallShape.y2,
                doorDist
              );
              const {
                x: dx,
                y: dy,
                nx,
                ny
              } = bezierPointAndNormal(
                wallShape.x,
                wallShape.y,
                wcx,
                wcy,
                wallShape.x2,
                wallShape.y2,
                t
              );
              const tx = -ny;
              const ty = nx;
              const hw = door.width / 2;
              return (
                <line
                  key={door.id}
                  x1={dx - hw * tx}
                  y1={dy - hw * ty}
                  x2={dx + hw * tx}
                  y2={dy + hw * ty}
                  stroke={DEFAULT_DOOR_COLOR}
                  strokeWidth={wallShape.thickness / 2}
                  strokeLinecap="butt"
                />
              );
            })}

            {segParams.map(([tStart, tEnd], i) => {
              const pStart = quadraticBezierPoint(
                wallShape.x,
                wallShape.y,
                wcx,
                wcy,
                wallShape.x2,
                wallShape.y2,
                tStart
              );
              const pEnd = quadraticBezierPoint(
                wallShape.x,
                wallShape.y,
                wcx,
                wcy,
                wallShape.x2,
                wallShape.y2,
                tEnd
              );
              // Approximate sub-curve control point by evaluating midpoint
              const tMid = (tStart + tEnd) / 2;
              const pMid = quadraticBezierPoint(
                wallShape.x,
                wallShape.y,
                wcx,
                wcy,
                wallShape.x2,
                wallShape.y2,
                tMid
              );
              // Derive a control point for the sub-segment Q curve: C = 2*midpoint - 0.5*(start + end)
              const subCx = 2 * pMid.x - 0.5 * (pStart.x + pEnd.x);
              const subCy = 2 * pMid.y - 0.5 * (pStart.y + pEnd.y);
              return (
                <path
                  key={`seg-${i}`}
                  d={`M ${pStart.x},${pStart.y} Q ${subCx},${subCy} ${pEnd.x},${pEnd.y}`}
                  stroke={wColor}
                  strokeWidth={wallShape.thickness}
                  strokeLinecap={hasDoors ? "round" : "butt"}
                  strokeDasharray={wDashStr}
                  fill="none"
                />
              );
            })}
            {wallShape.windows?.map((win) => {
              if (wLen === 0) return null;
              const arcLen = bezierArcLength(
                wallShape.x,
                wallShape.y,
                wcx,
                wcy,
                wallShape.x2,
                wallShape.y2
              );
              const winDist = win.position * arcLen;
              const t = bezierParameterAtDistance(
                wallShape.x,
                wallShape.y,
                wcx,
                wcy,
                wallShape.x2,
                wallShape.y2,
                winDist
              );
              const {
                x: wx,
                y: wy,
                nx,
                ny
              } = bezierPointAndNormal(
                wallShape.x,
                wallShape.y,
                wcx,
                wcy,
                wallShape.x2,
                wallShape.y2,
                t
              );
              const tx = -ny;
              const ty = nx;
              const hw = win.width / 2;
              return (
                <line
                  key={win.id}
                  x1={wx - hw * tx}
                  y1={wy - hw * ty}
                  x2={wx + hw * tx}
                  y2={wy + hw * ty}
                  stroke={DEFAULT_DOOR_COLOR}
                  strokeWidth={
                    wallShape.thickness - (wallShape.thickness * 20) / 100
                  }
                  strokeLinecap="butt"
                />
              );
            })}
            {wCapStart === "round" && (
              <circle
                cx={wallShape.x}
                cy={wallShape.y}
                r={wallShape.thickness / 2}
                fill={wColor}
              />
            )}
            {wCapEnd === "round" && (
              <circle
                cx={wallShape.x2}
                cy={wallShape.y2}
                r={wallShape.thickness / 2}
                fill={wColor}
              />
            )}
          </g>
        );
      }

      case "wall-path": {
        const pathShape = shape as IWallPathShape;
        const { vertices, edges, closed } = pathShape;
        const edgeCount = closed ? vertices.length : vertices.length - 1;
        const wallThickness = pathShape.thickness;
        const wallColor = pathShape.color || "#D1D5D9";
        const wpDash = getWallDash(pathShape.dashPattern, wallThickness);
        const wpDashStr = wpDash?.join(",");
        const wpCapStart = pathShape.lineCapStart || "butt";
        const wpCapEnd = pathShape.lineCapEnd || "butt";

        // Interior fill for closed wall-paths. Drawn first so wall strokes
        // sit on top of it. Curved edges are sampled along their bezier so
        // the fill outline follows the actual wall — otherwise a curved
        // edge gets cut across by a straight chord through its endpoints.
        const fillPolygon = (() => {
          if (!closed || !pathShape.fillColor || vertices.length < 3) {
            return null;
          }
          const FILL_SAMPLES = 20;
          const pts: string[] = [];
          for (let i = 0; i < vertices.length; i++) {
            const from = vertices[i];
            const to = vertices[(i + 1) % vertices.length];
            const edge = edges[i] || {};
            const curvature = edge.curvature || 0;
            if (curvature === 0) {
              pts.push(`${from.x},${from.y}`);
            } else {
              const { cx, cy } = getWallControlPoint(
                from.x,
                from.y,
                to.x,
                to.y,
                curvature,
                edge.curvatureCenter ?? 0.5
              );
              for (let s = 0; s < FILL_SAMPLES; s++) {
                const t = s / FILL_SAMPLES;
                const p = quadraticBezierPoint(
                  from.x,
                  from.y,
                  cx,
                  cy,
                  to.x,
                  to.y,
                  t
                );
                pts.push(`${p.x},${p.y}`);
              }
            }
          }
          return (
            <polygon
              points={pts.join(" ")}
              fill={pathShape.fillColor}
              opacity={pathShape.fillOpacity ?? 1}
            />
          );
        })();

        return (
          <g key={pathShape.id}>
            {fillPolygon}
            {Array.from({ length: edgeCount }).map((_, edgeIdx) => {
              const from = vertices[edgeIdx];
              const to = vertices[(edgeIdx + 1) % vertices.length];
              const edge = edges[edgeIdx] || {};
              const eDx = to.x - from.x;
              const eDy = to.y - from.y;
              const eLen = Math.sqrt(eDx * eDx + eDy * eDy);
              const eUx = eLen > 0 ? eDx / eLen : 0;
              const eUy = eLen > 0 ? eDy / eLen : 0;

              // Corner radius shortening
              const fromCR = from.cornerRadius || 0;
              const toCR = to.cornerRadius || 0;
              const fromHasTwoEdges = closed || edgeIdx > 0;
              const toHasTwoEdges = closed || edgeIdx + 1 < vertices.length - 1;
              // Overlap by 1px so the butt cap hides behind the arc
              const startOffset =
                fromHasTwoEdges && eLen > 0
                  ? Math.max(0, Math.min(fromCR, eLen / 2) - 1)
                  : 0;
              const endOffset =
                toHasTwoEdges && eLen > 0
                  ? Math.max(0, Math.min(toCR, eLen / 2) - 1)
                  : 0;

              const eCurvature = edge.curvature || 0;
              const eCurvatureCenter = edge.curvatureCenter ?? 0.5;
              const eHasDoors = edge.doors && edge.doors.length > 0;

              if (eCurvature === 0) {
                // Clamp wall segments to account for corner radius offsets
                let segments = getWallSegments(edge.doors, eLen);
                if (startOffset > 0 || endOffset > 0) {
                  segments = segments
                    .map(
                      ([s, e]) =>
                        [
                          Math.max(s, startOffset),
                          Math.min(e, eLen - endOffset)
                        ] as [number, number]
                    )
                    .filter(([s, e]) => s < e);
                }
                return (
                  <g key={`edge-${edgeIdx}`}>
                    {edge.doors?.map((door) => {
                      if (eLen === 0 || door.transparent) return null;
                      const hw = door.width / 2;
                      const cd = door.position * eLen;
                      return (
                        <line
                          key={door.id}
                          x1={from.x + (cd - hw) * eUx}
                          y1={from.y + (cd - hw) * eUy}
                          x2={from.x + (cd + hw) * eUx}
                          y2={from.y + (cd + hw) * eUy}
                          stroke={DEFAULT_DOOR_COLOR}
                          strokeWidth={wallThickness / 2}
                          strokeLinecap="butt"
                        />
                      );
                    })}
                    {segments.map(([start, end], i) => (
                      <line
                        key={`seg-${i}`}
                        x1={from.x + start * eUx}
                        y1={from.y + start * eUy}
                        x2={from.x + end * eUx}
                        y2={from.y + end * eUy}
                        stroke={wallColor}
                        strokeWidth={wallThickness}
                        strokeLinecap={eHasDoors ? "round" : "butt"}
                        strokeDasharray={wpDashStr}
                      />
                    ))}
                    {edge.windows?.map((win) => {
                      if (eLen === 0) return null;
                      const hw = win.width / 2;
                      const cd = win.position * eLen;
                      return (
                        <line
                          key={win.id}
                          x1={from.x + (cd - hw) * eUx}
                          y1={from.y + (cd - hw) * eUy}
                          x2={from.x + (cd + hw) * eUx}
                          y2={from.y + (cd + hw) * eUy}
                          stroke={DEFAULT_DOOR_COLOR}
                          strokeWidth={
                            wallThickness - (wallThickness * 20) / 100
                          }
                          strokeLinecap="butt"
                        />
                      );
                    })}
                  </g>
                );
              }

              // Curved edge
              const { cx: ecx, cy: ecy } = getWallControlPoint(
                from.x,
                from.y,
                to.x,
                to.y,
                eCurvature,
                eCurvatureCenter
              );
              const segParams = getCurvedWallSegmentParams(
                edge.doors,
                from.x,
                from.y,
                ecx,
                ecy,
                to.x,
                to.y
              );
              return (
                <g key={`edge-${edgeIdx}`}>
                  {edge.doors?.map((door) => {
                    if (eLen === 0 || door.transparent) return null;
                    const arcLen = bezierArcLength(
                      from.x,
                      from.y,
                      ecx,
                      ecy,
                      to.x,
                      to.y
                    );
                    const doorDist = door.position * arcLen;
                    const t = bezierParameterAtDistance(
                      from.x,
                      from.y,
                      ecx,
                      ecy,
                      to.x,
                      to.y,
                      doorDist
                    );
                    const {
                      x: dx,
                      y: dy,
                      nx,
                      ny
                    } = bezierPointAndNormal(
                      from.x,
                      from.y,
                      ecx,
                      ecy,
                      to.x,
                      to.y,
                      t
                    );
                    const tx = -ny;
                    const ty = nx;
                    const hw = door.width / 2;
                    return (
                      <line
                        key={door.id}
                        x1={dx - hw * tx}
                        y1={dy - hw * ty}
                        x2={dx + hw * tx}
                        y2={dy + hw * ty}
                        stroke={DEFAULT_DOOR_COLOR}
                        strokeWidth={wallThickness / 2}
                        strokeLinecap="butt"
                      />
                    );
                  })}
                  {segParams.map(([tStart, tEnd], i) => {
                    const pStart = quadraticBezierPoint(
                      from.x,
                      from.y,
                      ecx,
                      ecy,
                      to.x,
                      to.y,
                      tStart
                    );
                    const pEnd = quadraticBezierPoint(
                      from.x,
                      from.y,
                      ecx,
                      ecy,
                      to.x,
                      to.y,
                      tEnd
                    );
                    const tMid = (tStart + tEnd) / 2;
                    const pMid = quadraticBezierPoint(
                      from.x,
                      from.y,
                      ecx,
                      ecy,
                      to.x,
                      to.y,
                      tMid
                    );
                    const subCx = 2 * pMid.x - 0.5 * (pStart.x + pEnd.x);
                    const subCy = 2 * pMid.y - 0.5 * (pStart.y + pEnd.y);
                    return (
                      <path
                        key={`seg-${i}`}
                        d={`M ${pStart.x},${pStart.y} Q ${subCx},${subCy} ${pEnd.x},${pEnd.y}`}
                        stroke={wallColor}
                        strokeWidth={wallThickness}
                        strokeLinecap={eHasDoors ? "round" : "butt"}
                        strokeDasharray={wpDashStr}
                        fill="none"
                      />
                    );
                  })}
                  {edge.windows?.map((win) => {
                    if (eLen === 0) return null;
                    const arcLen = bezierArcLength(
                      from.x,
                      from.y,
                      ecx,
                      ecy,
                      to.x,
                      to.y
                    );
                    const winDist = win.position * arcLen;
                    const t = bezierParameterAtDistance(
                      from.x,
                      from.y,
                      ecx,
                      ecy,
                      to.x,
                      to.y,
                      winDist
                    );
                    const {
                      x: wx,
                      y: wy,
                      nx,
                      ny
                    } = bezierPointAndNormal(
                      from.x,
                      from.y,
                      ecx,
                      ecy,
                      to.x,
                      to.y,
                      t
                    );
                    const tx = -ny;
                    const ty = nx;
                    const hw = win.width / 2;
                    return (
                      <line
                        key={win.id}
                        x1={wx - hw * tx}
                        y1={wy - hw * ty}
                        x2={wx + hw * tx}
                        y2={wy + hw * ty}
                        stroke={DEFAULT_DOOR_COLOR}
                        strokeWidth={wallThickness - (wallThickness * 20) / 100}
                        strokeLinecap="butt"
                      />
                    );
                  })}
                </g>
              );
            })}
            {!closed && wpCapStart === "round" && vertices.length > 0 && (
              <circle
                cx={vertices[0].x}
                cy={vertices[0].y}
                r={wallThickness / 2}
                fill={wallColor}
              />
            )}
            {!closed && wpCapEnd === "round" && vertices.length > 1 && (
              <circle
                cx={vertices[vertices.length - 1].x}
                cy={vertices[vertices.length - 1].y}
                r={wallThickness / 2}
                fill={wallColor}
              />
            )}
            {/* Rounded corner arcs at vertices */}
            {vertices.map((v, vIdx) => {
              const cr = v.cornerRadius;
              if (!cr) return null;

              const prevIdx = closed
                ? (vIdx - 1 + vertices.length) % vertices.length
                : vIdx - 1;
              const nextIdx = (vIdx + 1) % vertices.length;
              const hasPrev = closed || vIdx > 0;
              const hasNext = closed || vIdx < vertices.length - 1;
              if (!hasPrev || !hasNext) return null;

              const prevV = vertices[prevIdx];
              const nextV = vertices[nextIdx];
              const toPrevDx = prevV.x - v.x;
              const toPrevDy = prevV.y - v.y;
              const toPrevLen = Math.sqrt(
                toPrevDx * toPrevDx + toPrevDy * toPrevDy
              );
              const toNextDx = nextV.x - v.x;
              const toNextDy = nextV.y - v.y;
              const toNextLen = Math.sqrt(
                toNextDx * toNextDx + toNextDy * toNextDy
              );
              if (toPrevLen === 0 || toNextLen === 0) return null;

              const r = Math.min(cr, toPrevLen / 2, toNextLen / 2);
              const p1x = v.x + (toPrevDx / toPrevLen) * r;
              const p1y = v.y + (toPrevDy / toPrevLen) * r;
              const p2x = v.x + (toNextDx / toNextLen) * r;
              const p2y = v.y + (toNextDy / toNextLen) * r;

              return (
                <path
                  key={`corner-${vIdx}`}
                  d={`M ${p1x},${p1y} Q ${v.x},${v.y} ${p2x},${p2y}`}
                  stroke={wallColor}
                  strokeWidth={wallThickness}
                  strokeLinecap="round"
                  fill="none"
                />
              );
            })}
          </g>
        );
      }

      default:
        return null;
    }
  };

  // Each shape's own transform now includes scaleX/scaleY (matching Konva's
  // `T(x,y) · R(θ) · S(sX, sY)` pipeline), so no outer flip wrapper is needed.
  // An outer wrapper applied AFTER per-shape rotation produces `flip · rotate`
  // instead of `rotate · scale`, which is mathematically different and was
  // causing flipped+rotated shapes to render mis-positioned vs. the editor.
  if (
    shape.type === "icon" ||
    shape.type === "furniture" ||
    shape.type === "spot"
  ) {
    return (
      <ShapeProvider key={shape.id} {...shapeProviderProps}>
        {renderShapeContent()}
      </ShapeProvider>
    );
  }

  return renderShapeContent();
};

const getShapesByLayer = (shapes: Shape[], layerType: LayerType): Shape[] => {
  // `hidden` is set per-shape from the editor's layers panel. Filtering here
  // keeps hidden shapes out of every downstream memo and out of the DOM —
  // both cheaper than rendering them with `display: none` or opacity 0.
  return shapes.filter(
    (shape) => shape.layerType === layerType && !shape.hidden
  );
};

/**
 * Per-shape memoized renderer. With many shapes on the canvas this is the
 * dominant cost: keeping the React tree stable between renders means the
 * reconciler can skip every shape whose data didn't change, and the browser
 * gets to keep its painted SVG buffer instead of re-rasterizing thousands
 * of strokes on each parent re-render.
 */
type ShapeSVGProps = {
  shape: Shape;
  zoneTypes?: { id: string; name: string; color: string }[];
  zoneColors?: Record<string, string>;
  visibleZoneCategories?: Set<string>;
  zoneCategoryHover?: string | null;
  iconCategoryHover?: string | null;
  onZoneLinkClick?: RenderOptions["onZoneLinkClick"];
  hoveredLinkZoneId?: string | null;
  onLinkZoneHover?: RenderOptions["onLinkZoneHover"];
  onLinkedIconClick?: RenderOptions["onLinkedIconClick"];
  onZoneTooltipHover?: RenderOptions["onZoneTooltipHover"];
};

// Shapes that actually consume the zone-only props: standalone zones,
// categorized or linked icons (points of interest / click-to-navigate),
// and composite spots whose companion list includes a zone. Shared by the
// ShapeSVG memo comparator below and the layer-building code that decides
// which shapes to pass these props to — kept as one function so the two
// can't drift out of sync (a shape the layer code treats as "consuming"
// but the comparator doesn't would silently skip re-renders).
const shapeConsumesLegendProps = (shape: Shape): boolean => {
  if (shape.type === "zone") return true;
  if (shape.type === "icon") {
    const icon = shape as IconShape;
    return !!icon.categoryTypeId || !!icon.link;
  }
  if (shape.type === "spot") {
    const spot = shape as SpotShape;
    return !!spot.companions?.some((c) => c.type === "zone");
  }
  return false;
};

const ShapeSVG = React.memo<ShapeSVGProps>(
  ({
    shape,
    zoneTypes,
    zoneColors,
    visibleZoneCategories,
    zoneCategoryHover,
    iconCategoryHover,
    onZoneLinkClick,
    hoveredLinkZoneId,
    onLinkZoneHover,
    onLinkedIconClick,
    onZoneTooltipHover
  }) => {
    const rendered = renderShapeToSVG(shape, {
      zoneTypes,
      zoneColors,
      visibleZoneCategories,
      zoneCategoryHover,
      iconCategoryHover,
      onZoneLinkClick,
      hoveredLinkZoneId,
      onLinkZoneHover,
      onLinkedIconClick,
      onZoneTooltipHover
    });
    if (!rendered) return null;
    return (
      <g data-construction-shape={shape.type} data-construction-layer={shape.layerType}>
        {rendered}
      </g>
    );
  },
  // Custom areEqual: only zones (and composite spots whose companions
  // include zones) consume the legend-related props. For every other
  // shape type we ignore those props entirely and compare only the
  // shape reference. That way a legend hover (which changes
  // `zoneCategoryHover` on every onMouseEnter/onMouseLeave) does NOT
  // invalidate the thousands of walls, spots, and icons on the map —
  // they short-circuit here regardless of whether the call site
  // bothered to omit the zone-only props.
  (prev, next) => {
    if (prev.shape !== next.shape) return false;
    if (!shapeConsumesLegendProps(next.shape)) return true;
    return (
      prev.zoneTypes === next.zoneTypes &&
      prev.zoneColors === next.zoneColors &&
      prev.visibleZoneCategories === next.visibleZoneCategories &&
      prev.zoneCategoryHover === next.zoneCategoryHover &&
      prev.iconCategoryHover === next.iconCategoryHover &&
      prev.onZoneLinkClick === next.onZoneLinkClick &&
      prev.hoveredLinkZoneId === next.hoveredLinkZoneId &&
      prev.onLinkZoneHover === next.onLinkZoneHover &&
      prev.onLinkedIconClick === next.onLinkedIconClick &&
      prev.onZoneTooltipHover === next.onZoneTooltipHover
    );
  }
);
ShapeSVG.displayName = "ShapeSVG";

export const ViewSVG = React.forwardRef<SVGSVGElement, ViewSVGProps>(
  (
    {
      showOutside = true,
      svgOverlay,
      style,
      hideZoneLegend = false,
      legendBottomOffset,
      bridgeZoneLegend = false,
      onLegendDataChange,
      legendControlRef,
      focusBounds
    },
    ref
  ) => {
    const {
      editingZoneWidth,
      editingZoneHeight,
      zoneTypes,
      zoneColors,
      isViewMode,
      onZoneLinkClick,
      pixelsPerMeter,
      interactiveZones,
      authenticatedUser
    } = useMapCanvas();
    // Zone area (m²) is admin-only in the legend — same permission the app
    // already gates map-editing access on elsewhere.
    const isAdmin = !!authenticatedUser?.permissions.settings;
    const { shapes } = useMapCanvasShapes();
    // Set of category ids the viewer has activated via the legend.
    // Default empty — every category-tagged zone stays hidden until the
    // user opts in. Non-persistent (resets on reload).
    const [toggledZoneCategories, setVisibleZoneCategories] = React.useState<
      Set<string>
    >(new Set());
    // A cropped preview has no legend to toggle from: every category shows.
    const visibleZoneCategories = React.useMemo(
      () =>
        focusBounds
          ? new Set((zoneTypes ?? []).map((t) => t.id))
          : toggledZoneCategories,
      [focusBounds, zoneTypes, toggledZoneCategories]
    );
    const toggleZoneCategory = React.useCallback((id: string) => {
      setVisibleZoneCategories((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    }, []);
    // Legend hover spotlight: brightens zones of the hovered category,
    // dims the rest of the currently-visible zones. Distinct from the
    // visibility toggle so the user can scan a busy map quickly.
    const [zoneCategoryHover, setZoneCategoryHover] = React.useState<
      string | null
    >(null);
    // Hovered navigation zone (map hover or legend hover) — brightened so
    // it reads as a clickable target.
    const [hoveredLinkZoneId, setHoveredLinkZoneId] = React.useState<
      string | null
    >(null);
    // Zone currently hovered on the map — drives the info tooltip
    // (name/category/area). Tracks live cursor position, not the shape's
    // bounds, since the tooltip should follow the mouse while hovering.
    const [hoveredZoneTooltip, setHoveredZoneTooltip] = React.useState<{
      zone: ZoneShape;
      clientX: number;
      clientY: number;
    } | null>(null);
    // Hovered points-of-interest category (legend hover) — matching icons
    // get a pulsing halo so they're easy to locate on the map.
    const [iconCategoryHover, setIconCategoryHover] = React.useState<
      string | null
    >(null);
    // Icon whose link description popover is open, plus its real viewport
    // bounding box (captured at click time) so the popover — portaled to
    // document.body — can position itself in viewport space instead of
    // guessing from world coordinates (which breaks under ancestor CSS
    // scaling, e.g. a "fit map to container" wrapper). Icons with a
    // description show this popover instead of navigating immediately.
    const [activeLinkedIcon, setActiveLinkedIcon] = React.useState<{
      icon: IconShape;
      rect: DOMRect;
    } | null>(null);
    const handleLinkedIconClick = React.useCallback(
      (icon: IconShape, rect: DOMRect) => {
        if (!icon.link) return;
        if (icon.link.description) {
          setActiveLinkedIcon((prev) =>
            prev?.icon.id === icon.id ? null : { icon, rect }
          );
        } else {
          // Navigating away — close any other icon's popover that might
          // still be open instead of leaving it stranded over the map.
          setActiveLinkedIcon(null);
          onZoneLinkClick?.(icon.link);
        }
      },
      [onZoneLinkClick]
    );
    const { stagePosition } = useMapCanvasStagePosition();
    // The popover's position is a one-time getBoundingClientRect()
    // snapshot from click time — panning already closes it via the
    // pointerdown "click outside" listener, but wheel zoom and the zoom
    // controls change stagePosition without firing that path, so without
    // this the popover drifts away from the icon it was anchored to.
    React.useEffect(() => {
      setActiveLinkedIcon(null);
    }, [stagePosition]);
    const width = editingZoneWidth;
    const height = editingZoneHeight;

    // A cropped preview shows the focus rectangle with a margin of a tenth
    // of its size on every side, and the SVG scales to its container.
    const focusViewBox = React.useMemo(() => {
      if (!focusBounds) return undefined;
      const { x, y, width: w, height: h } = focusBounds;
      const marginX = Math.max(w * 0.1, 10);
      const marginY = Math.max(h * 0.1, 10);
      return `${x - marginX} ${y - marginY} ${w + marginX * 2} ${h + marginY * 2}`;
    }, [focusBounds]);

    // Apply stage transformations
    const transform = `translate(${stagePosition.x}, ${stagePosition.y}) scale(${stagePosition.scale})`;

    // Layer partitioning depends only on `shapes`. Including stagePosition
    // or editing-zone size here causes every pan/zoom/resize to invalidate
    // every downstream memo and re-render thousands of shapes — that was
    // the cliff that pinned interactive use at ~1 fps.
    const processedShapes = React.useMemo(() => {
      return {
        floorShapes: getShapesByLayer(shapes, "floor"),
        backgroundShapes: getShapesByLayer(shapes, "background"),
        shapeShapes: getShapesByLayer(shapes, "shapes"),
        spotShapes: getShapesByLayer(shapes, "spots")
      };
    }, [shapes]);

    // Wall segments are scanned by both the floor-fill and junction passes;
    // compute once and share so we walk the shapes array a single time
    // instead of three times per render.
    const allWallSegments = React.useMemo(
      () => getAllWallSegments(shapes),
      [shapes]
    );

    // Zones with a navigation link — listed in the legend so they're
    // reachable without hunting for them on the map.
    const linkedZones = React.useMemo(
      () =>
        shapes.filter(
          (shape): shape is ZoneShape =>
            shape.type === "zone" &&
            !!(shape as ZoneShape).link &&
            !shape.hidden
        ),
      [shapes]
    );

    // Points of interest: categorized icons grouped by category, with a
    // per-map count and a representative icon for the legend row glyph.
    const pointsOfInterest = React.useMemo(() => {
      const byCategory = new Map<
        string,
        { count: number; sample: IconShape }
      >();
      const walk = (list: Shape[]) => {
        for (const item of list) {
          if (item.hidden) continue;
          if (item.type === "icon") {
            const icon = item as IconShape;
            if (icon.categoryTypeId) {
              const entry = byCategory.get(icon.categoryTypeId);
              if (entry) entry.count += 1;
              else
                byCategory.set(icon.categoryTypeId, { count: 1, sample: icon });
            }
          } else if (item.type === "group") {
            walk((item as GroupShape).children || []);
          } else if (item.type === "spot") {
            walk(((item as SpotShape).companions as Shape[]) || []);
          }
        }
      };
      walk(shapes);
      return zoneTypes
        .filter((t) => byCategory.has(t.id))
        .map((t) => {
          const entry = byCategory.get(t.id) as {
            count: number;
            sample: IconShape;
          };
          return { category: t, count: entry.count, sample: entry.sample };
        });
    }, [shapes, zoneTypes]);

    // Zone categories that actually paint zones on this map — the legend
    // splits them from icon-only (points-of-interest) categories.
    const zoneCategoryIds = React.useMemo(() => {
      const ids = new Set<string>();
      const walk = (list: Shape[]) => {
        for (const item of list) {
          if (item.hidden) continue;
          if (item.type === "zone") {
            const zone = item as ZoneShape;
            if (zone.categoryTypeId) ids.add(zone.categoryTypeId);
          } else if (item.type === "group") {
            walk((item as GroupShape).children || []);
          } else if (item.type === "spot") {
            walk(((item as SpotShape).companions as Shape[]) || []);
          }
        }
      };
      walk(shapes);
      return ids;
    }, [shapes]);

    // Per-category total area in real-world m², for the legend — only
    // computed when the map is calibrated (pixelsPerMeter set).
    const zoneAreasByCategory = React.useMemo(() => {
      if (!pixelsPerMeter || !isAdmin) return null;
      const totals = new Map<string, number>();
      const walk = (list: Shape[]) => {
        for (const item of list) {
          if (item.hidden) continue;
          if (item.type === "zone") {
            const zone = item as ZoneShape;
            if (!zone.categoryTypeId) continue;
            const areaSqm = computeZoneAreaPx(zone) / pixelsPerMeter ** 2;
            totals.set(
              zone.categoryTypeId,
              (totals.get(zone.categoryTypeId) || 0) + areaSqm
            );
          } else if (item.type === "group") {
            walk((item as GroupShape).children || []);
          } else if (item.type === "spot") {
            walk(((item as SpotShape).companions as Shape[]) || []);
          }
        }
      };
      walk(shapes);
      return totals;
    }, [shapes, pixelsPerMeter, isAdmin]);

    // All spot shapes on the map, by id (including group-nested) — used to
    // resolve a zone's containedSpotIds into actual spots for the counts
    // below. Not admin-gated, unlike area.
    const spotsById = React.useMemo(() => {
      const map = new Map<string, SpotShape>();
      const walk = (list: Shape[]) => {
        for (const item of list) {
          // Matches the other legend/tooltip memos (e.g. zoneAreasByCategory)
          // which already skip hidden shapes — without this, a hidden spot
          // still referenced by a zone's containedSpotIds would count toward
          // desk/meeting-room totals for a spot that isn't visible.
          if (item.hidden) continue;
          if (item.type === "spot") {
            map.set(item.id, item as SpotShape);
          } else if (item.type === "group") {
            walk((item as GroupShape).children || []);
          }
        }
      };
      walk(shapes);
      return map;
    }, [shapes]);

    // Desk and meeting-room counts per zone category, for the legend —
    // mirrors zoneAreasByCategory's shape/traversal but isn't admin-gated.
    const zoneResourceCountsByCategory = React.useMemo(() => {
      const totals = new Map<string, { desks: number; meetingRooms: number }>();
      const walk = (list: Shape[]) => {
        for (const item of list) {
          if (item.hidden) continue;
          if (item.type === "zone") {
            const zone = item as ZoneShape;
            if (!zone.categoryTypeId || !zone.containedSpotIds?.length)
              continue;
            const entry = totals.get(zone.categoryTypeId) || {
              desks: 0,
              meetingRooms: 0
            };
            for (const spotId of zone.containedSpotIds) {
              const spot = spotsById.get(spotId);
              if (!spot) continue;
              if (
                spot.spotType === SpotShapeType.Desk ||
                spot.spotType === SpotShapeType.Node
              ) {
                entry.desks++;
              } else if (spot.spotType === SpotShapeType.Room) {
                entry.meetingRooms++;
              }
            }
            totals.set(zone.categoryTypeId, entry);
          } else if (item.type === "group") {
            walk((item as GroupShape).children || []);
          } else if (item.type === "spot") {
            walk(((item as SpotShape).companions as Shape[]) || []);
          }
        }
      };
      walk(shapes);
      return totals;
    }, [shapes, spotsById]);

    // Legend bridge: exposes the same actions the in-webview ZoneLegend
    // triggers on itself, so an external (native) panel can drive this
    // component's own state without duplicating any zone/shape parsing.
    React.useImperativeHandle(
      legendControlRef,
      () => ({
        toggleZoneCategory,
        setVisibleZoneCategories: (categoryIds: string[]) =>
          setVisibleZoneCategories(new Set(categoryIds)),
        setZoneCategoryHover,
        setIconCategoryHover,
        triggerLinkedZone: (zoneId: string) => {
          const zone = linkedZones.find((z) => z.id === zoneId);
          if (zone?.link) onZoneLinkClick?.(zone.link);
        }
      }),
      [toggleZoneCategory, linkedZones, onZoneLinkClick]
    );

    // Legend bridge: reports the same content the in-webview ZoneLegend
    // would render, recomputed whenever the underlying map data or the
    // viewer's own visibility state changes.
    React.useEffect(() => {
      if (!bridgeZoneLegend || !onLegendDataChange) return;

      const categories = zoneCategoryIds
        ? zoneTypes.filter((t) => zoneCategoryIds.has(t.id))
        : zoneTypes;
      const zoneCategoriesData: LegendZoneCategoryData[] = categories.map(
        (t) => {
          const counts = zoneResourceCountsByCategory.get(t.id);
          return {
            id: t.id,
            name: t.name,
            color: t.color,
            isVisible: visibleZoneCategories.has(t.id),
            desks: counts?.desks ?? 0,
            meetingRooms: counts?.meetingRooms ?? 0,
            areaSqm: zoneAreasByCategory?.get(t.id) ?? null
          };
        }
      );

      let totalDesks = 0;
      let totalMeetingRooms = 0;
      for (const value of zoneResourceCountsByCategory.values()) {
        totalDesks += value.desks;
        totalMeetingRooms += value.meetingRooms;
      }
      let totalAreaSqm: number | null = null;
      if (zoneAreasByCategory) {
        totalAreaSqm = 0;
        for (const value of zoneAreasByCategory.values()) {
          totalAreaSqm += value;
        }
      }

      // Same gate as the in-webview legend's own "actionableLinkedZones" —
      // no navigation handler, no point listing zones that go nowhere.
      const linkedZonesData = onZoneLinkClick
        ? linkedZones.map((zone) => ({
            id: zone.id,
            name: zone.name || zone.link?.label || "Open map",
            color: zone.color ?? null
          }))
        : [];

      onLegendDataChange({
        zoneCategories: zoneCategoriesData,
        pointsOfInterest: pointsOfInterest.map(
          ({ category, count, sample }) => ({
            id: category.id,
            name: category.name,
            count,
            iconUrl: sample.iconUrl ?? null
          })
        ),
        linkedZones: linkedZonesData,
        totalDesks,
        totalMeetingRooms,
        totalAreaSqm
      });
    }, [
      bridgeZoneLegend,
      onLegendDataChange,
      zoneCategoryIds,
      zoneTypes,
      zoneResourceCountsByCategory,
      visibleZoneCategories,
      zoneAreasByCategory,
      pointsOfInterest,
      linkedZones,
      onZoneLinkClick
    ]);

    // Desk headcount for the currently-hovered zone: total desks/workspaces
    // it contains (via containedSpotIds, computed at save time), and how
    // many are free vs busy right now — cross-referencing each contained
    // spot's resource_id against interactiveZones (the live occupancy data
    // the map already loads for spot coloring; `disabled` there means the
    // resource has zero available slots right now). Desks with no
    // interactiveZones entry (no live data) count toward the total but not
    // free/busy.
    const hoveredZoneDeskStats = React.useMemo(() => {
      const zone = hoveredZoneTooltip?.zone;
      const containedIds = zone?.containedSpotIds;
      if (!containedIds?.length) return null;
      // Same source (spotsById, all spots) and same type filter the legend's
      // zoneResourceCountsByCategory uses — getSpotsWithResourceId would
      // silently drop desks that aren't linked to a resource yet, giving
      // this tooltip a different total than the legend for the same zone.
      const deskSpots = Array.from(containedIds)
        .map((id) => spotsById.get(id))
        .filter(
          (spot): spot is SpotShape =>
            !!spot &&
            (spot.spotType === SpotShapeType.Desk ||
              spot.spotType === SpotShapeType.Node)
        );
      if (deskSpots.length === 0) return null;

      let free = 0;
      let busy = 0;
      for (const spot of deskSpots) {
        const interactiveZone = spot.resource_id
          ? interactiveZones?.[spot.resource_id]
          : undefined;
        if (!interactiveZone || interactiveZone.notAssigned) continue;
        // Dedicated desks are permanently claimed by one person — not
        // bookable by anyone else regardless of whether they currently
        // have an active schedule, so they always count as busy.
        if (interactiveZone.disabled || interactiveZone.isDedicated) busy++;
        else free++;
      }
      return { total: deskSpots.length, free, busy };
    }, [hoveredZoneTooltip?.zone, spotsById, interactiveZones]);

    // Pass the zone-only props only to shapes that actually consume them
    // (see shapeConsumesLegendProps). Every other shape receives
    // `undefined` for these props, which is reference-stable across
    // hover/filter changes — so `React.memo` on `ShapeSVG` can skip the
    // thousands of walls, spots, and icons that don't care about the
    // legend state.
    const consumesLegendProps = shapeConsumesLegendProps;

    const bottomLayerSVG = React.useMemo(
      () =>
        [
          ...processedShapes.floorShapes,
          ...processedShapes.backgroundShapes
        ].map((shape) =>
          consumesLegendProps(shape) ? (
            <ShapeSVG
              key={shape.id}
              shape={shape}
              zoneTypes={zoneTypes}
              zoneColors={zoneColors}
              visibleZoneCategories={visibleZoneCategories}
              zoneCategoryHover={zoneCategoryHover}
              iconCategoryHover={iconCategoryHover}
              onZoneLinkClick={onZoneLinkClick}
              hoveredLinkZoneId={hoveredLinkZoneId}
              onLinkZoneHover={setHoveredLinkZoneId}
              onLinkedIconClick={handleLinkedIconClick}
              onZoneTooltipHover={setHoveredZoneTooltip}
            />
          ) : (
            <ShapeSVG key={shape.id} shape={shape} />
          )
        ),
      [
        processedShapes.floorShapes,
        processedShapes.backgroundShapes,
        zoneTypes,
        zoneColors,
        visibleZoneCategories,
        zoneCategoryHover,
        iconCategoryHover,
        onZoneLinkClick,
        hoveredLinkZoneId,
        handleLinkedIconClick,
        setHoveredZoneTooltip
      ]
    );

    const topLayerSVG = React.useMemo(
      () =>
        processedShapes.shapeShapes.map((shape) =>
          consumesLegendProps(shape) ? (
            <ShapeSVG
              key={shape.id}
              shape={shape}
              zoneTypes={zoneTypes}
              zoneColors={zoneColors}
              visibleZoneCategories={visibleZoneCategories}
              zoneCategoryHover={zoneCategoryHover}
              iconCategoryHover={iconCategoryHover}
              onZoneLinkClick={onZoneLinkClick}
              hoveredLinkZoneId={hoveredLinkZoneId}
              onLinkZoneHover={setHoveredLinkZoneId}
              onLinkedIconClick={handleLinkedIconClick}
              onZoneTooltipHover={setHoveredZoneTooltip}
            />
          ) : (
            <ShapeSVG key={shape.id} shape={shape} />
          )
        ),
      [
        processedShapes.shapeShapes,
        zoneTypes,
        zoneColors,
        visibleZoneCategories,
        zoneCategoryHover,
        iconCategoryHover,
        onZoneLinkClick,
        hoveredLinkZoneId,
        handleLinkedIconClick,
        setHoveredZoneTooltip
      ]
    );

    const stopLayerSVG = React.useMemo(
      () =>
        processedShapes.spotShapes.map((shape) =>
          consumesLegendProps(shape) ? (
            <ShapeSVG
              key={shape.id}
              shape={shape}
              zoneTypes={zoneTypes}
              zoneColors={zoneColors}
              visibleZoneCategories={visibleZoneCategories}
              zoneCategoryHover={zoneCategoryHover}
              iconCategoryHover={iconCategoryHover}
              onZoneLinkClick={onZoneLinkClick}
              hoveredLinkZoneId={hoveredLinkZoneId}
              onLinkZoneHover={setHoveredLinkZoneId}
              onLinkedIconClick={handleLinkedIconClick}
              onZoneTooltipHover={setHoveredZoneTooltip}
            />
          ) : (
            <ShapeSVG key={shape.id} shape={shape} />
          )
        ),
      [
        processedShapes.spotShapes,
        zoneTypes,
        zoneColors,
        visibleZoneCategories,
        zoneCategoryHover,
        iconCategoryHover,
        onZoneLinkClick,
        hoveredLinkZoneId,
        handleLinkedIconClick,
        setHoveredZoneTooltip
      ]
    );

    const wallFloorFillSVG = React.useMemo(() => {
      if (allWallSegments.length < 3) return null;
      const polygons = computeWallFloorPolygons(allWallSegments);
      return polygons.map((poly, i) => (
        <polygon
          key={`wall-floor-fill-${i}`}
          points={poly.points.map((p) => `${p.x},${p.y}`).join(" ")}
          fill="white"
        />
      ));
    }, [allWallSegments]);

    const wallJunctionSVG = React.useMemo(() => {
      if (allWallSegments.length < 2) return null;
      const junctions = findWallJunctions(allWallSegments);
      const roundedVertices = shapes
        .filter((s): s is IWallPathShape => s.type === "wall-path")
        .flatMap((s) =>
          s.vertices.filter((v) => v.cornerRadius && v.cornerRadius > 0)
        );
      return junctions
        .filter(
          (j) =>
            !roundedVertices.some(
              (v) =>
                Math.abs(v.x - j.center.x) < 2 && Math.abs(v.y - j.center.y) < 2
            )
        )
        .map((j, i) => (
          <polygon
            key={`wall-junction-${i}`}
            points={j.points.map((p) => `${p.x},${p.y}`).join(" ")}
            fill={j.color}
          />
        ));
    }, [allWallSegments, shapes]);

    return (
      <div
        className="map-builder-svg-wrapper"
        style={
          focusViewBox
            ? {
                position: "relative",
                display: "block",
                width: "100%",
                height: "100%"
              }
            : { position: "relative", display: "inline-block" }
        }
      >
        <svg
          width={focusViewBox ? "100%" : width}
          height={focusViewBox ? "100%" : height}
          viewBox={focusViewBox ?? `0 0 ${width} ${height}`}
          preserveAspectRatio={focusViewBox ? "xMidYMid meet" : undefined}
          xmlns="http://www.w3.org/2000/svg"
          className={"injected-svg map-builder-svg"}
          ref={ref}
          style={style}
        >
          {/* Single transform wrapper for everything inside the canvas. Chrome
           *  composites this whole subtree as one GPU layer, so panning/zooming
           *  doesn't trigger a tree-wide Layerize pass. `will-change: transform`
           *  is the explicit hint; without it, Chrome promotes/demotes the
           *  layer based on heuristics that can be expensive to re-evaluate. */}
          <g
            transform={focusViewBox ? undefined : transform}
            style={{ willChange: "transform" }}
          >
            <g data-construction-decoration="floor">{wallFloorFillSVG}</g>
            {bottomLayerSVG}
            <g data-construction-decoration="junctions">{wallJunctionSVG}</g>
            {topLayerSVG}
            {/* Slot where the host app portals its hidden hover/focus pulse
             *  templates (inside the SVG so cloning keeps the SVG
             *  namespace). Live pulses are clones inserted into the target
             *  spot's own group — above its body, below its dot. */}
            <g id={"pulse-layer"} />
            <g id={"resources"}>{stopLayerSVG}</g>
            {svgOverlay}
          </g>
        </svg>
        {activeLinkedIcon?.icon.link && (
          <IconLinkPopover
            rect={activeLinkedIcon.rect}
            link={activeLinkedIcon.icon.link}
            onOpenLink={
              onZoneLinkClick
                ? () => {
                    onZoneLinkClick(activeLinkedIcon.icon.link!);
                    setActiveLinkedIcon(null);
                  }
                : undefined
            }
            onClose={() => setActiveLinkedIcon(null)}
          />
        )}
        {hoveredZoneTooltip && (
          <ZoneHoverTooltip
            x={hoveredZoneTooltip.clientX}
            y={hoveredZoneTooltip.clientY}
            name={hoveredZoneTooltip.zone.name || "Zone"}
            categoryName={
              zoneTypes?.find(
                (t) => t.id === hoveredZoneTooltip.zone.categoryTypeId
              )?.name
            }
            areaSqm={
              pixelsPerMeter && isAdmin
                ? computeZoneAreaPx(hoveredZoneTooltip.zone) /
                  pixelsPerMeter ** 2
                : undefined
            }
            deskStats={hoveredZoneDeskStats}
          />
        )}
        {/* Zone-type legend — viewer only (suppressed in map builder /
         *  edit mode). The legend itself is its own component so the
         *  HTML / DOM concerns don't bloat this SVG orchestrator. */}
        {isViewMode && !hideZoneLegend && (
          <ZoneLegend
            zoneTypes={zoneTypes}
            zoneCategoryIds={zoneCategoryIds}
            pointsOfInterest={pointsOfInterest}
            setIconCategoryHover={setIconCategoryHover}
            visibleZoneCategories={visibleZoneCategories}
            setVisibleZoneCategories={setVisibleZoneCategories}
            toggleZoneCategory={toggleZoneCategory}
            setZoneCategoryHover={setZoneCategoryHover}
            linkedZones={linkedZones}
            onLinkedZoneClick={onZoneLinkClick}
            setHoveredLinkZoneId={setHoveredLinkZoneId}
            zoneAreasByCategory={zoneAreasByCategory}
            zoneResourceCountsByCategory={zoneResourceCountsByCategory}
            bottomOffset={legendBottomOffset}
          />
        )}
      </div>
    );
  }
);
